import gql from "graphql-tag";
import type { ApolloClient } from "@apollo/client";

import type {
  DeleteBankEmployerMutation,
  DeleteBankEmployerMutationVariables,
  DeleteBankIndividualMutation,
  DeleteBankIndividualMutationVariables,
  GetIndividualReauthorizationLinkTokenQuery,
  GetIndividualReauthorizationLinkTokenQueryVariables,
  GetPlaidLinkTokenQuery,
  GetPlaidLinkTokenQueryVariables,
  GetReauthorizationLinkTokenQuery,
  GetReauthorizationLinkTokenQueryVariables,
  LinkBankIndividualMutation,
  LinkBankIndividualMutationVariables,
  LinkBankMutation,
  LinkBankMutationVariables,
  UpdateLinkTokenMutation,
  UpdateLinkTokenMutationVariables,
  VerifyBankAccountMutation,
  VerifyBankAccountMutationVariables,
} from "types/graphql";

// link bank with Icon
function linkBank(
  client: ApolloClient<object>,
  token: string,
  metadata: LinkBankMutationVariables["metadata"]
): Promise<LinkBankMutation["linkBank"]> {
  return new Promise((resolve, reject) => {
    const LINK_BANK = gql`
      mutation linkBank($public_token: String!, $metadata: MetaData!) {
        linkBank(public_token: $public_token, metadata: $metadata) {
          status
          data
          userBank {
            id
            bankName
            bankAlias
            verificationStatus
            accountType
            accountSubtype
            accountId
            correlationId
            state
          }
        }
      }
    `;
    const result = client.mutate<LinkBankMutation, LinkBankMutationVariables>({
      mutation: LINK_BANK,
      variables: {
        public_token: token,
        metadata,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.linkBank);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

// link bank with Icon
function linkBankIndividual(
  client: ApolloClient<object>,
  token: string,
  metadata: LinkBankIndividualMutationVariables["metadata"],
  ownerName: string
): Promise<LinkBankIndividualMutation["linkBankIndividual"]> {
  return new Promise((resolve, reject) => {
    const LINK_BANK = gql`
      mutation linkBankIndividual(
        $public_token: String!
        $metadata: MetaData!
        $ownerName: String!
      ) {
        linkBankIndividual(
          public_token: $public_token
          metadata: $metadata
          ownerName: $ownerName
        ) {
          status
          data
          userBank {
            id
            bankName
            bankAlias
            verificationStatus
            accountType
            accountSubtype
            accountId
            correlationId
            state
          }
        }
      }
    `;
    const result = client.mutate<
      LinkBankIndividualMutation,
      LinkBankIndividualMutationVariables
    >({
      mutation: LINK_BANK,
      variables: {
        public_token: token,
        metadata,
        ownerName,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.linkBankIndividual);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function deleteBankIndividual(
  client: ApolloClient<object>,
  accountId: string
): Promise<DeleteBankIndividualMutation> {
  return new Promise((resolve, reject) => {
    const DELETE_BANK = gql`
      mutation deleteBankIndividual($accountId: ID!) {
        deleteBankIndividual(accountId: $accountId)
      }
    `;
    const result = client.mutate<
      DeleteBankIndividualMutation,
      DeleteBankIndividualMutationVariables
    >({
      mutation: DELETE_BANK,
      variables: {
        accountId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function deleteBankEmployer(
  client: ApolloClient<object>,
  accountId: string
): Promise<DeleteBankEmployerMutation> {
  return new Promise((resolve, reject) => {
    const DELETE_BANK = gql`
      mutation deleteBankEmployer($accountId: ID!) {
        deleteBankEmployer(accountId: $accountId)
      }
    `;
    const result = client.mutate<
      DeleteBankEmployerMutation,
      DeleteBankEmployerMutationVariables
    >({
      mutation: DELETE_BANK,
      variables: {
        accountId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function getPlaidLinkToken(
  client: ApolloClient<object>,
  accountId?: string | null
): Promise<GetPlaidLinkTokenQuery["getPlaidLinkToken"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getPlaidLinkToken($accountId: String) {
        getPlaidLinkToken(accountId: $accountId) {
          linkToken
        }
      }
    `;
    const result = client.query<
      GetPlaidLinkTokenQuery,
      GetPlaidLinkTokenQueryVariables
    >({
      query: REQUEST,
      variables: { accountId },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getPlaidLinkToken);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function verifyBankAccountRequest(
  client: ApolloClient<object>,
  accountId: string
): Promise<VerifyBankAccountMutation["verifyBankAccount"]> {
  return new Promise((resolve, reject) => {
    const MUTATION = gql`
      mutation verifyBankAccount($accountId: ID!) {
        verifyBankAccount(accountId: $accountId) {
          id
          bankName
          bankAlias
          verificationStatus
          accountType
          accountSubtype
          accountId
          correlationId
          state
        }
      }
    `;
    const result = client.mutate<
      VerifyBankAccountMutation,
      VerifyBankAccountMutationVariables
    >({
      mutation: MUTATION,
      variables: {
        accountId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.verifyBankAccount);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function getReauthorizationLinkToken(
  client: ApolloClient<object>,
  accountId: string
): Promise<GetReauthorizationLinkTokenQuery["getReauthorizationLinkToken"]> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getReauthorizationLinkToken($accountId: String!) {
        getReauthorizationLinkToken(accountId: $accountId) {
          linkToken
        }
      }
    `;
    const result = client.query<
      GetReauthorizationLinkTokenQuery,
      GetReauthorizationLinkTokenQueryVariables
    >({
      query: REQUEST,
      variables: { accountId },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getReauthorizationLinkToken);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

function updateLinkToken(
  client: ApolloClient<object>,
  accountId: string
): Promise<UpdateLinkTokenMutation> {
  return new Promise((resolve, reject) => {
    const UPDATE_LINK_TOKEN = gql`
      mutation updateLinkToken($accountId: ID!) {
        updateLinkToken(accountId: $accountId)
      }
    `;
    const result = client.mutate<
      UpdateLinkTokenMutation,
      UpdateLinkTokenMutationVariables
    >({
      mutation: UPDATE_LINK_TOKEN,
      variables: {
        accountId,
      },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        console.error(error);
        reject(error);
      }
    );
  });
}

function getIndividualReauthorizationLinkToken(
  client: ApolloClient<object>,
  accountId: string
): Promise<
  GetIndividualReauthorizationLinkTokenQuery["getIndividualReauthorizationLinkToken"]
> {
  return new Promise((resolve, reject) => {
    const REQUEST = gql`
      query getIndividualReauthorizationLinkToken($accountId: String!) {
        getIndividualReauthorizationLinkToken(accountId: $accountId) {
          linkToken
        }
      }
    `;
    const result = client.query<
      GetIndividualReauthorizationLinkTokenQuery,
      GetIndividualReauthorizationLinkTokenQueryVariables
    >({
      query: REQUEST,
      variables: { accountId },
    });
    result.then(
      (response) => {
        if (response.data) {
          resolve(response.data.getIndividualReauthorizationLinkToken);
        } else {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- pre-existing contract: rejects the raw Apollo response
          reject(response);
        }
      },
      (error: Error) => {
        reject(error);
      }
    );
  });
}

export default {
  linkBank,
  linkBankIndividual,
  deleteBankIndividual,
  deleteBankEmployer,
  getPlaidLinkToken,
  verifyBankAccountRequest,
  getReauthorizationLinkToken,
  updateLinkToken,
  getIndividualReauthorizationLinkToken,
};
